import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, ExternalLink, Clock, Users, CreditCard, Flame } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_NAMES = { starter: "Base", pro: "Pro", institutional: "Institutional" };
const EXPLORER_URLS = {
  "TRC20 (Tron)": (tx) => `https://tronscan.org/#/transaction/${tx}`,
  "Bitcoin (BTC)": (tx) => `https://blockstream.info/tx/${tx}`,
  "ERC20 (Ethereum)": (tx) => `https://etherscan.io/tx/${tx}`,
  "BEP20 (BNB Smart Chain)": (tx) => `https://bscscan.com/tx/${tx}`,
};

const SERVICE_LABELS = {
  alphabot: "Alphabot",
  trubon: "Trubon",
  roar: "Roar",
  "account-handling": "Trading Account Handling",
  "funded-passing": "Funded Account Passing",
  "crypto-services": "Crypto & Cash Services",
};

const SERVICE_COLORS = {
  alphabot: "text-[#00E676] bg-[#00E676]/10 border-[#00E676]/20",
  trubon: "text-[#007AFF] bg-[#007AFF]/10 border-[#007AFF]/20",
  roar: "text-[#FF6D00] bg-[#FF6D00]/10 border-[#FF6D00]/20",
  "account-handling": "text-[#FFC107] bg-[#FFC107]/10 border-[#FFC107]/20",
  "funded-passing": "text-[#E040FB] bg-[#E040FB]/10 border-[#E040FB]/20",
  "crypto-services": "text-[#FF5252] bg-[#FF5252]/10 border-[#FF5252]/20",
};

const STATUS_BADGE = {
  pending: <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-medium flex items-center gap-1"><Clock size={10} /> Pending</span>,
  approved: <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs font-medium flex items-center gap-1"><CheckCircle size={10} /> Approved</span>,
  rejected: <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs font-medium flex items-center gap-1"><XCircle size={10} /> Rejected</span>,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("waitlist");

  // Payment state
  const [payments, setPayments] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState("pending");

  // Waitlist state
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(true);
  const [waitlistActionLoading, setWaitlistActionLoading] = useState(null);
  const [serviceFilter, setServiceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("pending");

  // Roar allocations state
  const [roarAllocations, setRoarAllocations] = useState([]);
  const [roarLoading, setRoarLoading] = useState(true);
  const [roarConfigured, setRoarConfigured] = useState(true);

  const fetchPayments = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/"); return; }
    try {
      const res = await axios.get(`${API}/admin/payments/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments(res.data.payments || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Owner access required.");
        navigate("/");
      } else {
        toast.error("Failed to load payments.");
      }
    } finally {
      setPaymentLoading(false);
    }
  }, [navigate]);

  const fetchWaitlist = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/"); return; }
    try {
      const res = await axios.get(`${API}/admin/waitlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWaitlistEntries(res.data.entries || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Owner access required.");
        navigate("/");
      } else {
        toast.error("Failed to load waitlist.");
      }
    } finally {
      setWaitlistLoading(false);
    }
  }, [navigate]);

  const fetchRoarAllocations = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/"); return; }
    try {
      const res = await axios.get(`${API}/admin/roar/allocations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoarAllocations(res.data.allocations || []);
      setRoarConfigured(res.data.configured !== false);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Owner access required.");
        navigate("/");
      } else {
        toast.error("Failed to load Roar accounts.");
      }
    } finally {
      setRoarLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPayments();
    fetchWaitlist();
    fetchRoarAllocations();
  }, [fetchPayments, fetchWaitlist, fetchRoarAllocations]);

  const handlePaymentAction = async (orderId, approve) => {
    const token = localStorage.getItem("token");
    setActionLoading(orderId);
    try {
      await axios.post(
        `${API}/admin/payments/approve`,
        { order_id: orderId, approve },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approve ? "Subscription activated!" : "Payment rejected.");
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleWaitlistAction = async (entryId, approve) => {
    const token = localStorage.getItem("token");
    setWaitlistActionLoading(entryId);
    try {
      await axios.post(
        `${API}/admin/waitlist/approve`,
        { waitlist_id: entryId, approve },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approve ? "Waitlist request approved!" : "Waitlist request rejected.");
      fetchWaitlist();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed.");
    } finally {
      setWaitlistActionLoading(null);
    }
  };

  const filteredPayments = payments.filter(p => paymentFilter === "all" || p.status === paymentFilter);

  const filteredWaitlist = waitlistEntries.filter(e => {
    const serviceMatch = serviceFilter === "all" || e.service === serviceFilter;
    const statusMatch = statusFilter === "all" || e.status === statusFilter;
    return serviceMatch && statusMatch;
  });

  const services = [...new Set(waitlistEntries.map(e => e.service))];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="pt-28 pb-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-1">Owner Dashboard</h1>
            <p className="text-[#A1A1AA]">Manage waitlist requests and crypto payments.</p>
          </div>

          {/* Tab Switcher */}
          <div className="flex gap-2 mb-8 border-b border-[#27272A] pb-3">
            <button
              onClick={() => setActiveTab("waitlist")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === "waitlist"
                  ? "bg-[#00E676] text-black"
                  : "bg-[#1C1C1E] text-[#A1A1AA] hover:bg-[#27272A]"
              }`}
            >
              <Users size={16} />
              Waitlist
              {waitlistEntries.filter(e => e.status === "pending").length > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === "waitlist" ? "bg-black/20 text-black" : "bg-yellow-500/20 text-yellow-400"
                }`}>
                  {waitlistEntries.filter(e => e.status === "pending").length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("payments")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === "payments"
                  ? "bg-[#00E676] text-black"
                  : "bg-[#1C1C1E] text-[#A1A1AA] hover:bg-[#27272A]"
              }`}
            >
              <CreditCard size={16} />
              Payments
              {payments.filter(p => p.status === "pending").length > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === "payments" ? "bg-black/20 text-black" : "bg-yellow-500/20 text-yellow-400"
                }`}>
                  {payments.filter(p => p.status === "pending").length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("roar")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === "roar"
                  ? "bg-[#FF6D00] text-black"
                  : "bg-[#1C1C1E] text-[#A1A1AA] hover:bg-[#27272A]"
              }`}
            >
              <Flame size={16} />
              Roar Accounts
              {roarAllocations.length > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === "roar" ? "bg-black/20 text-black" : "bg-[#FF6D00]/20 text-[#FF9100]"
                }`}>
                  {roarAllocations.length}
                </span>
              )}
            </button>
          </div>

          {/* ============== WAITLIST TAB ============== */}
          {activeTab === "waitlist" && (
            <div>
              {/* Service filter cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                <button
                  onClick={() => setServiceFilter("all")}
                  className={`border rounded-xl p-3 text-center capitalize font-semibold transition-all text-sm ${
                    serviceFilter === "all"
                      ? "bg-white/10 border-white/30 text-white ring-2 ring-white/20"
                      : "bg-[#111111] border-[#27272A] text-[#A1A1AA] hover:bg-[#1C1C1E]"
                  }`}
                >
                  <div className="text-xl font-bold">{waitlistEntries.length}</div>
                  <div className="text-xs mt-1">All</div>
                </button>
                {Object.entries(SERVICE_LABELS).map(([key, label]) => {
                  const count = waitlistEntries.filter(e => e.service === key).length;
                  const colorClass = SERVICE_COLORS[key] || "text-white bg-white/10 border-white/20";
                  return (
                    <button
                      key={key}
                      onClick={() => setServiceFilter(key)}
                      className={`border rounded-xl p-3 text-center transition-all text-sm ${colorClass} ${
                        serviceFilter === key ? "ring-2 ring-current" : "opacity-70 hover:opacity-100"
                      }`}
                    >
                      <div className="text-xl font-bold">{count}</div>
                      <div className="text-xs mt-1 truncate">{label}</div>
                    </button>
                  );
                })}
              </div>

              {/* Status filter */}
              <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
                {["pending", "approved", "rejected", "all"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setStatusFilter(f)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-all whitespace-nowrap ${
                      statusFilter === f
                        ? "bg-[#00E676] text-black"
                        : "bg-[#1C1C1E] text-[#A1A1AA] hover:bg-[#27272A]"
                    }`}
                  >
                    {f} ({f === "all"
                      ? (serviceFilter === "all" ? waitlistEntries : waitlistEntries.filter(e => e.service === serviceFilter)).length
                      : (serviceFilter === "all" ? waitlistEntries : waitlistEntries.filter(e => e.service === serviceFilter)).filter(e => e.status === f).length
                    })
                  </button>
                ))}
              </div>

              {waitlistLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="w-8 h-8 text-[#00E676] animate-spin" />
                </div>
              ) : filteredWaitlist.length === 0 ? (
                <div className="text-center py-16 text-[#52525B]">
                  No waitlist entries found.
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredWaitlist.map((entry) => {
                    const colorClass = SERVICE_COLORS[entry.service] || "";
                    return (
                      <div key={entry.id} className="bg-[#111111] border border-[#27272A] rounded-xl p-5">
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3 flex-wrap">
                              {STATUS_BADGE[entry.status]}
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
                                {SERVICE_LABELS[entry.service] || entry.service}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                              <div>
                                <div className="text-[#52525B] text-xs mb-0.5">Email</div>
                                <div className="text-white">{entry.email}</div>
                              </div>
                              <div>
                                <div className="text-[#52525B] text-xs mb-0.5">Service</div>
                                <div className="font-semibold">{SERVICE_LABELS[entry.service] || entry.service}</div>
                              </div>
                              <div>
                                <div className="text-[#52525B] text-xs mb-0.5">Submitted</div>
                                <div>{entry.submitted_at ? new Date(entry.submitted_at).toLocaleString() : "—"}</div>
                              </div>
                            </div>
                            {entry.status === "approved" && entry.service === "alphabot" && (
                              <div className="mt-2 px-3 py-2 bg-[#00E676]/10 border border-[#00E676]/20 rounded-lg text-xs text-[#00E676]">
                                This user can now see USDT & BTC payment options in Alphabot pricing.
                              </div>
                            )}
                            {entry.status === "approved" && entry.service === "roar" && (
                              <div className="mt-2 px-3 py-2 bg-[#FF6D00]/10 border border-[#FF6D00]/20 rounded-lg text-xs text-[#FF9100]">
                                User can access Roar once an allocation is created via roar_propagate.py locally.
                              </div>
                            )}
                          </div>

                          {entry.status === "pending" && (
                            <div className="flex gap-2 md:flex-col">
                              <Button
                                onClick={() => handleWaitlistAction(entry.id, true)}
                                disabled={waitlistActionLoading === entry.id}
                                className="bg-[#00E676] hover:bg-[#00C853] text-black text-sm px-6"
                              >
                                {waitlistActionLoading === entry.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  "Approve"
                                )}
                              </Button>
                              <Button
                                onClick={() => handleWaitlistAction(entry.id, false)}
                                disabled={waitlistActionLoading === entry.id}
                                variant="outline"
                                className="border-red-500/40 text-red-400 hover:bg-red-500/10 text-sm px-6"
                              >
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-6 text-center">
                <button onClick={fetchWaitlist} className="text-sm text-[#A1A1AA] hover:text-white transition-colors">
                  Refresh
                </button>
              </div>
            </div>
          )}

          {/* ============== ROAR ACCOUNTS TAB ============== */}
          {activeTab === "roar" && (
            <div>
              {!roarConfigured && (
                <div className="mb-6 px-4 py-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-sm text-yellow-400">
                  ROAR_MONGO_URL is not configured on the backend. Set it in backend/.env to enable Roar data.
                </div>
              )}
              {roarLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="w-8 h-8 text-[#FF6D00] animate-spin" />
                </div>
              ) : roarAllocations.length === 0 ? (
                <div className="text-center py-16 text-[#52525B]">
                  No Roar allocations yet. Run <code className="text-[#FF9100]">python backend/roar_propagate.py</code> locally to seed data.
                </div>
              ) : (
                <div className="space-y-3">
                  {roarAllocations.map((a) => (
                    <div key={a.user_id} className="bg-[#111111] border border-[#27272A] rounded-xl p-5">
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 text-sm">
                        <div>
                          <div className="text-[#52525B] text-xs mb-0.5">Email</div>
                          <div className="text-white truncate">{a.email}</div>
                        </div>
                        <div>
                          <div className="text-[#52525B] text-xs mb-0.5">Deposit</div>
                          <div className="font-data text-[#00E676]">${Number(a.deposit || 0).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-[#52525B] text-xs mb-0.5">Profit</div>
                          <div className="font-data text-[#FF9100]">${Number(a.profit || 0).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-[#52525B] text-xs mb-0.5">Earnings ({a.profit_split || 70}%)</div>
                          <div className="font-data text-[#FF6D00]">${Number(a.earnings || 0).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-[#52525B] text-xs mb-0.5">Plan</div>
                          <div>{a.plan_label || a.plan || "—"}</div>
                        </div>
                        <div>
                          <div className="text-[#52525B] text-xs mb-0.5">Start date</div>
                          <div className="text-xs">{a.start_date_label || "—"}</div>
                        </div>
                        <div>
                          <div className="text-[#52525B] text-xs mb-0.5">Payout date</div>
                          <div className="text-xs">{a.payout_date_label || "—"}</div>
                        </div>
                        <div>
                          <div className="text-[#52525B] text-xs mb-0.5">Updated</div>
                          <div className="text-xs">{a.updated_at ? new Date(a.updated_at).toLocaleString() : "—"}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-6 text-center">
                <button onClick={fetchRoarAllocations} className="text-sm text-[#A1A1AA] hover:text-white transition-colors">
                  Refresh
                </button>
              </div>
            </div>
          )}

          {/* ============== PAYMENTS TAB ============== */}
          {activeTab === "payments" && (
            <div>
              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
                {["pending", "approved", "rejected"].map((s) => {
                  const count = payments.filter(p => p.status === s).length;
                  const colors = {
                    pending: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
                    approved: "text-green-400 bg-green-500/10 border-green-500/20",
                    rejected: "text-red-400 bg-red-500/10 border-red-500/20",
                  };
                  return (
                    <button
                      key={s}
                      onClick={() => setPaymentFilter(s)}
                      className={`${colors[s]} border rounded-xl p-4 text-center capitalize font-semibold transition-all ${paymentFilter === s ? "ring-2 ring-current" : "opacity-70 hover:opacity-100"}`}
                    >
                      <div className="text-2xl font-bold">{count}</div>
                      <div className="text-sm mt-1">{s}</div>
                    </button>
                  );
                })}
              </div>

              {/* Filter Tabs */}
              <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
                {["pending", "approved", "rejected", "all"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setPaymentFilter(f)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-all ${paymentFilter === f ? "bg-[#00E676] text-black" : "bg-[#1C1C1E] text-[#A1A1AA] hover:bg-[#27272A]"}`}
                  >
                    {f}
                  </button>
                ))}
              </div>

              {paymentLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="w-8 h-8 text-[#00E676] animate-spin" />
                </div>
              ) : filteredPayments.length === 0 ? (
                <div className="text-center py-16 text-[#52525B]">
                  No {paymentFilter === "all" ? "" : paymentFilter} payments found.
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredPayments.map((p) => {
                    const explorerFn = EXPLORER_URLS[p.network];
                    const explorerUrl = explorerFn ? explorerFn(p.tx_hash) : null;
                    return (
                      <div key={p.id} className="bg-[#111111] border border-[#27272A] rounded-xl p-5">
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3 flex-wrap">
                              {STATUS_BADGE[p.status]}
                              <span className="text-[#A1A1AA] text-xs font-mono">#{p.order_id}</span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div>
                                <div className="text-[#52525B] text-xs mb-0.5">User</div>
                                <div className="text-white truncate">{p.user_email || "—"}</div>
                              </div>
                              <div>
                                <div className="text-[#52525B] text-xs mb-0.5">Plan</div>
                                <div className="text-[#00E676] font-semibold">{PLAN_NAMES[p.plan] || p.plan}</div>
                              </div>
                              <div>
                                <div className="text-[#52525B] text-xs mb-0.5">Coin / Network</div>
                                <div>{p.coin} · {p.network}</div>
                              </div>
                              <div>
                                <div className="text-[#52525B] text-xs mb-0.5">Submitted</div>
                                <div>{p.submitted_at ? new Date(p.submitted_at).toLocaleString() : "—"}</div>
                              </div>
                            </div>
                            <div>
                              <div className="text-[#52525B] text-xs mb-0.5">TxHash</div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs text-white truncate max-w-[150px] sm:max-w-xs">{p.tx_hash}</span>
                                {explorerUrl && (
                                  <a href={explorerUrl} target="_blank" rel="noopener noreferrer"
                                    className="text-[#00E676] hover:underline">
                                    <ExternalLink size={13} />
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>

                          {p.status === "pending" && (
                            <div className="flex gap-2 md:flex-col">
                              <Button
                                onClick={() => handlePaymentAction(p.order_id, true)}
                                disabled={actionLoading === p.order_id}
                                className="bg-[#00E676] hover:bg-[#00C853] text-black text-sm px-6"
                              >
                                {actionLoading === p.order_id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Approve"}
                              </Button>
                              <Button
                                onClick={() => handlePaymentAction(p.order_id, false)}
                                disabled={actionLoading === p.order_id}
                                variant="outline"
                                className="border-red-500/40 text-red-400 hover:bg-red-500/10 text-sm px-6"
                              >
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-6 text-center">
                <button onClick={fetchPayments} className="text-sm text-[#A1A1AA] hover:text-white transition-colors">
                  Refresh
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
